import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Download, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const BrandModels = ({ canWrite }) => {
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(null);
  const [newBrand, setNewBrand] = useState("");
  const [newModel, setNewModel] = useState({});
  const [sqftDraft, setSqftDraft] = useState({});
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/config/brands").then(({ data }) => setBrands(data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (brand, patch) => {
    try {
      await api.put(`/config/brands/${brand.id}`, {
        name: brand.name,
        models: brand.models || [],
        ppf_sqft: brand.ppf_sqft || {},
        premium: brand.premium === true,
        active: brand.active !== false,
        ...patch,
      });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const addBrand = async () => {
    try {
      await api.post("/config/brands", { name: newBrand.trim(), models: [], active: true });
      toast.success(`${newBrand.trim()} added`);
      setNewBrand("");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const addModel = async (brand) => {
    const model = (newModel[brand.id] || "").trim();
    if (!model) return;
    if ((brand.models || []).includes(model)) {
      toast.error(`${model} is already listed under ${brand.name}`);
      return;
    }
    try {
      const { data } = await api.post(`/config/brands/${brand.id}/models`, { model });
      setBrands((prev) =>
        prev.map((b) => (b.id === brand.id ? { ...b, models: data.models, ppf_sqft: data.ppf_sqft } : b)));
      setNewModel((m) => ({ ...m, [brand.id]: "" }));
      toast.success(`${model} added under ${brand.name}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveSqft = async (brand, model) => {
    const raw = (sqftDraft[brand.id] ?? {})[model];
    const value = Number(raw === undefined ? (brand.ppf_sqft || {})[model] || 0 : raw);
    try {
      const { data } = await api.put(`/config/brands/${brand.id}/sqft`, { model, sqft: value });
      // Update just this brand locally from the authoritative response, and clear the draft so the
      // input reflects the persisted value (survives refresh).
      setBrands((prev) =>
        prev.map((b) => (b.id === brand.id ? { ...b, ppf_sqft: data.ppf_sqft } : b)));
      setSqftDraft((d) => {
        const bd = { ...(d[brand.id] || {}) };
        delete bd[model];
        return { ...d, [brand.id]: bd };
      });
      toast.success(`${brand.name} ${model} — ${data.sqft} sqft PPF`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeModel = async (brand, model) => {
    try {
      const { data } = await api.delete(`/config/brands/${brand.id}/models`, { params: { model } });
      setBrands((prev) =>
        prev.map((b) => (b.id === brand.id ? { ...b, models: data.models, ppf_sqft: data.ppf_sqft } : b)));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeBrand = async (brand) => {
    try {
      await api.delete(`/config/brands/${brand.id}`);
      toast.success(`${brand.name} removed`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const downloadCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [["Brand", "Premium", "Model", "PPF sqft"]];
    brands.forEach((b) => {
      const prem = b.premium === true ? "Premium" : "Standard";
      const models = b.models || [];
      if (models.length === 0) {
        rows.push([b.name, prem, "", ""]);
      } else {
        models.forEach((m) => rows.push([b.name, prem, m, (b.ppf_sqft || {})[m] ?? ""]));
      }
    });
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `veloskin-car-brands-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Car brand & model list downloaded");
  };

  const parseCsv = (text) => {
    const rows = [];
    let field = "";
    let record = [];
    let inQuotes = false;
    const t = text.replace(/^\uFEFF/, "");
    for (let i = 0; i < t.length; i += 1) {
      const ch = t[i];
      if (inQuotes) {
        if (ch === '"') {
          if (t[i + 1] === '"') { field += '"'; i += 1; } else { inQuotes = false; }
        } else field += ch;
      } else if (ch === '"') inQuotes = true;
      else if (ch === ",") { record.push(field); field = ""; }
      else if (ch === "\n" || ch === "\r") {
        if (ch === "\r" && t[i + 1] === "\n") i += 1;
        record.push(field); rows.push(record); field = ""; record = [];
      } else field += ch;
    }
    if (field !== "" || record.length) { record.push(field); rows.push(record); }
    return rows.filter((r) => r.some((c) => (c || "").trim() !== ""));
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const grid = parseCsv(text);
      if (grid.length < 2) throw new Error("empty");
      const header = grid[0].map((h) => (h || "").toLowerCase().trim());
      const col = (kw) => header.findIndex((h) => h.includes(kw));
      const bi = col("brand"); const pi = col("premium");
      const mi = col("model"); const si = col("sqft");
      if (bi < 0) { toast.error("CSV needs a 'Brand' column"); setImporting(false); return; }
      const rows = grid.slice(1).map((r) => {
        const premRaw = pi >= 0 ? (r[pi] || "").toLowerCase().trim() : "";
        return {
          brand: (r[bi] || "").trim(),
          model: mi >= 0 ? (r[mi] || "").trim() : "",
          sqft: si >= 0 ? Number((r[si] || "0").replace(/[^\d.]/g, "")) || 0 : 0,
          premium: premRaw === "" ? null : premRaw.startsWith("prem"),
        };
      }).filter((r) => r.brand);
      if (!rows.length) { toast.error("No brand rows found in the file"); setImporting(false); return; }
      const { data } = await api.post("/config/brands/bulk-import", { rows });
      toast.success(
        `Imported: ${data.brands_created} brand(s), ${data.models_added} model(s), ${data.sqft_set} sqft value(s)`);
      if ((data.errors || []).length) toast.error(`${data.errors.length} row(s) skipped — check names for '.' or '$'`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not read that file — use the CSV format");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="brand-models-panel">
      <div className="mb-4 flex flex-wrap justify-end gap-2">
        {canWrite && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="import-brands-csv-input"
              onChange={(e) => importCsv(e.target.files?.[0])}
            />
            <Button
              variant="outline"
              className="min-h-[44px]"
              data-testid="import-brands-csv-btn"
              onClick={() => fileRef.current?.click()}
              disabled={importing}
            >
              <Upload className="mr-2 h-4 w-4" /> {importing ? "Importing…" : "Import from CSV"}
            </Button>
          </>
        )}
        <Button
          variant="outline"
          className="min-h-[44px]"
          data-testid="download-brands-csv-btn"
          onClick={downloadCsv}
          disabled={brands.length === 0}
        >
          <Download className="mr-2 h-4 w-4" /> Download brand &amp; model list (CSV)
        </Button>
      </div>
      {canWrite && (
        <Card className="mb-4 border-brand-line shadow-none">
          <CardHeader>
            <CardTitle className="text-base">Add a car brand</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <div>
              <Label>Brand name</Label>
              <Input
                className="mt-1.5 w-64 min-h-[44px]"
                data-testid="new-brand-input"
                value={newBrand}
                placeholder="e.g. BMW"
                onChange={(e) => setNewBrand(e.target.value)}
              />
            </div>
            <Button
              className="min-h-[44px]"
              data-testid="add-brand-btn"
              onClick={addBrand}
              disabled={!newBrand.trim()}
            >
              <Plus className="mr-2 h-4 w-4" /> Add brand
            </Button>
            <p className="w-full text-xs text-slate-500">
              Add the brand first, then open it to add its models and each model's standard PPF
              sqft. Job and customer records pick a brand, then only that brand's models are
              offered, and the PPF sqft pre-fills on quotes and jobs.
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="border-brand-line shadow-none">
        <CardContent className="divide-y divide-slate-100 p-0">
          {brands.length === 0 && (
            <p className="py-12 text-center text-sm text-slate-500">No brands yet.</p>
          )}
          {brands.map((b) => {
            const isOpen = open === b.id;
            return (
              <div key={b.id} data-testid={`brand-${b.id}`}>
                <div className="flex flex-wrap items-center gap-3 p-4">
                  <button
                    className="flex flex-1 items-center gap-2 text-left"
                    data-testid={`brand-toggle-${b.id}`}
                    onClick={() => setOpen(isOpen ? null : b.id)}
                  >
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    )}
                    <span className="font-medium text-brand-ink">{b.name}</span>
                    <Badge className="border-0 bg-slate-100 text-slate-600">
                      {(b.models || []).length} model(s)
                    </Badge>
                    {b.active === false && (
                      <Badge className="border-0 bg-slate-100 text-slate-500">Inactive</Badge>
                    )}
                    {b.premium === true && (
                      <Badge className="border-0 bg-amber-100 text-amber-900" data-testid={`brand-premium-badge-${b.id}`}>
                        Premium
                      </Badge>
                    )}
                  </button>
                  {canWrite && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className={`min-h-[44px] ${b.premium === true ? "border-amber-300 bg-amber-50 text-amber-900" : ""}`}
                        data-testid={`brand-toggle-premium-${b.id}`}
                        onClick={() => save(b, { premium: b.premium !== true })}
                      >
                        {b.premium === true ? "Premium ✓" : "Mark Premium"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-h-[44px]"
                        data-testid={`brand-toggle-active-${b.id}`}
                        onClick={() => save(b, { active: b.active === false })}
                      >
                        {b.active === false ? "Activate" : "Deactivate"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-h-[44px] border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                        data-testid={`brand-delete-${b.id}`}
                        onClick={() => removeBrand(b)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {isOpen && (
                  <div className="bg-slate-50/60 px-4 pb-4" data-testid={`brand-panel-${b.id}`}>
                    <div className="flex flex-wrap gap-2" data-testid={`models-${b.id}`}>
                      {(b.models || []).length === 0 && (
                        <p className="text-sm text-slate-500">
                          No models under {b.name} yet.
                        </p>
                      )}
                      {(b.models || []).map((m) => (
                        <div
                          key={m}
                          data-testid={`model-${b.id}-${m}`}
                          className="flex items-center gap-2 rounded-md border border-brand-line bg-white px-3 py-2 text-sm text-slate-700"
                        >
                          <span className="font-medium">{m}</span>
                          <span className="text-xs text-slate-400">PPF</span>
                          <Input
                            className="h-9 w-20 text-right"
                            inputMode="decimal"
                            disabled={!canWrite}
                            data-testid={`model-sqft-${b.id}-${m}`}
                            value={(sqftDraft[b.id] ?? {})[m] ?? (b.ppf_sqft || {})[m] ?? ""}
                            onChange={(e) =>
                              setSqftDraft((d) => ({
                                ...d,
                                [b.id]: { ...(d[b.id] || {}), [m]: e.target.value },
                              }))
                            }
                          />
                          <span className="text-xs text-slate-400">sqft</span>
                          {canWrite && (
                            <>
                              <button
                                data-testid={`model-sqft-save-${b.id}-${m}`}
                                onClick={() => saveSqft(b, m)}
                                className="text-xs font-semibold text-brand underline"
                              >
                                Save
                              </button>
                              <button
                                data-testid={`model-remove-${b.id}-${m}`}
                                onClick={() => removeModel(b, m)}
                                className="text-slate-400 transition-colors duration-200 hover:text-rose-600"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    {canWrite && (
                      <div className="mt-3 flex flex-wrap items-end gap-2">
                        <Input
                          className="w-56 min-h-[44px]"
                          placeholder={`New model for ${b.name}`}
                          data-testid={`new-model-input-${b.id}`}
                          value={newModel[b.id] || ""}
                          onChange={(e) =>
                            setNewModel((m) => ({ ...m, [b.id]: e.target.value }))
                          }
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="min-h-[44px]"
                          data-testid={`add-model-btn-${b.id}`}
                          onClick={() => addModel(b)}
                          disabled={!(newModel[b.id] || "").trim()}
                        >
                          <Plus className="mr-1.5 h-3.5 w-3.5" /> Add model
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};
